import {
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  CHECK_LIST,
  CODE,
  HEADING,
  INLINE_CODE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  LINK,
  ORDERED_LIST,
  QUOTE,
  STRIKETHROUGH,
  UNORDERED_LIST,
  $convertFromMarkdownString,
  $convertToMarkdownString,
  ElementTransformer,
  TextMatchTransformer,
  Transformer,
} from '@lexical/markdown';
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from '@lexical/react/LexicalHorizontalRuleNode';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import { $isParagraphNode, $isTextNode, LexicalNode, TextNode } from 'lexical';

// Custom INLINE_CODE: match full `...` (both backticks) so the closing backtick
// is not left as literal text in list items (e.g. "- content`" -> "- `content`").
const INLINE_CODE_FULL: TextMatchTransformer = {
  dependencies: [TextNode],
  export: (node) => {
    if (!$isTextNode(node)) return null;
    if (node.hasFormat('code')) {
      const text = node.getTextContent();
      return text ? '`' + text + '`' : null;
    }
    return null;
  },
  importRegExp: /`([^`]+)`/,
  regExp: /(?!)/,
  replace: (textNode, match) => {
    textNode.setFormat('code');
    textNode.setTextContent(match[1]);
  },
  trigger: '`',
  type: 'text-match',
};

// Horizontal rule: ---, ***, ___ (required for round-trip; not in default TRANSFORMERS)
const HORIZONTAL_RULE: Transformer = {
  dependencies: [HorizontalRuleNode],
  export: (node) => ($isHorizontalRuleNode(node) ? '---' : null),
  regExp: /^(---|\*\*\*|___)\s?$/,
  replace: (parentNode, _children, match, _isImport) => {
    const line = $createHorizontalRuleNode();
    parentNode.replace(line);
  },
  type: 'element',
};

// Custom transformer for underline: <u>text</u>
const UNDERLINE: TextMatchTransformer = {
  dependencies: [TextNode],
  export: (node) => {
    if (!$isTextNode(node)) return null;
    if (node.hasFormat('underline')) {
      return `<u>${node.getTextContent()}</u>`;
    }
    return null;
  },
  importRegExp: /<u>([\s\S]*?)<\/u>/,
  regExp: /(?!)/,
  replace: (textNode, match) => {
    textNode.setFormat('underline');
    textNode.setTextContent(match[1]);
  },
  trigger: '>',
  type: 'text-match',
};

// Custom transformer for subscript: <sub>text</sub>
const SUBSCRIPT: TextMatchTransformer = {
  dependencies: [TextNode],
  export: (node) => {
    if (!$isTextNode(node)) return null;
    if (node.hasFormat('subscript')) {
      return `<sub>${node.getTextContent()}</sub>`;
    }
    return null;
  },
  importRegExp: /<sub>([\s\S]*?)<\/sub>/,
  regExp: /(?!)/,
  replace: (textNode, match) => {
    textNode.setFormat('subscript');
    textNode.setTextContent(match[1]);
  },
  trigger: '>',
  type: 'text-match',
};

// Custom transformer for superscript: <sup>text</sup>
const SUPERSCRIPT: TextMatchTransformer = {
  dependencies: [TextNode],
  export: (node) => {
    if (!$isTextNode(node)) return null;
    if (node.hasFormat('superscript')) {
      return `<sup>${node.getTextContent()}</sup>`;
    }
    return null;
  },
  importRegExp: /<sup>([\s\S]*?)<\/sup>/,
  regExp: /(?!)/,
  replace: (textNode, match) => {
    textNode.setFormat('superscript');
    textNode.setTextContent(match[1]);
  },
  trigger: '>',
  type: 'text-match',
};

// GitHub-flavored markdown tables. Ported from the Lexical Playground TABLE
// transformer (not in @lexical/markdown's default set), adapted to use this
// editor's transformer list for cell content. Import assembles consecutive
// `| … |` lines into a TableNode and promotes the row before a `| --- |`
// divider to a header; export emits standard GFM pipe syntax. Cell text is
// trimmed so the conventional `| a | b |` spacing round-trips exactly.
const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)\s?$/;
const TABLE_ROW_DIVIDER_REG_EXP = /^(\| ?:?-*:? ?)+\|\s?$/;

const createTableCell = (textContent: string): TableCellNode => {
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
  $convertFromMarkdownString(textContent.replace(/\\n/g, '\n').trim(), MARKDOWN_TRANSFORMERS, cell);
  return cell;
};

const mapToTableCells = (textContent: string): Array<TableCellNode> | null => {
  const match = textContent.match(TABLE_ROW_REG_EXP);
  if (!match || !match[1]) return null;
  return match[1].split('|').map((text) => createTableCell(text));
};

const getTableColumnsSize = (table: TableNode): number => {
  const row = table.getFirstChild();
  return $isTableRowNode(row) ? row.getChildrenSize() : 0;
};

const TABLE: ElementTransformer = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) return null;
    const output: string[] = [];
    for (const row of node.getChildren()) {
      if (!$isTableRowNode(row)) continue;
      const rowOutput: string[] = [];
      let isHeaderRow = false;
      for (const cell of row.getChildren()) {
        if ($isTableCellNode(cell)) {
          rowOutput.push(
            $convertToMarkdownString(MARKDOWN_TRANSFORMERS, cell).replace(/\n/g, '\\n'),
          );
          if (cell.__headerState === TableCellHeaderStates.ROW) isHeaderRow = true;
        }
      }
      output.push(`| ${rowOutput.join(' | ')} |`);
      if (isHeaderRow) output.push(`| ${rowOutput.map(() => '---').join(' | ')} |`);
    }
    return output.join('\n');
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _children, match) => {
    // The `| --- | --- |` divider promotes the previous row to a header, then is dropped.
    if (TABLE_ROW_DIVIDER_REG_EXP.test(match[0])) {
      const table = parentNode.getPreviousSibling();
      if (!$isTableNode(table)) return;
      const rows = table.getChildren();
      const lastRow = rows[rows.length - 1];
      if (!$isTableRowNode(lastRow)) return;
      lastRow.getChildren().forEach((cell) => {
        if ($isTableCellNode(cell)) {
          cell.setHeaderStyles(TableCellHeaderStates.ROW, TableCellHeaderStates.ROW);
        }
      });
      parentNode.remove();
      return;
    }

    const matchCells = mapToTableCells(match[0]);
    if (matchCells == null) return;

    const rows = [matchCells];
    let sibling = parentNode.getPreviousSibling();
    let maxCells = matchCells.length;
    // Walk back over preceding paragraph lines that are also table rows.
    while (sibling) {
      if (!$isParagraphNode(sibling) || sibling.getChildrenSize() !== 1) break;
      const firstChild = sibling.getFirstChild();
      if (!$isTextNode(firstChild)) break;
      const cells = mapToTableCells(firstChild.getTextContent());
      if (cells == null) break;
      maxCells = Math.max(maxCells, cells.length);
      rows.unshift(cells);
      const prev = sibling.getPreviousSibling();
      sibling.remove();
      sibling = prev;
    }

    const table = $createTableNode();
    for (const cells of rows) {
      const tableRow = $createTableRowNode();
      table.append(tableRow);
      for (let i = 0; i < maxCells; i++) {
        tableRow.append(i < cells.length ? cells[i] : createTableCell(''));
      }
    }

    const previousSibling = parentNode.getPreviousSibling();
    if ($isTableNode(previousSibling) && getTableColumnsSize(previousSibling) === maxCells) {
      previousSibling.append(...table.getChildren());
      parentNode.remove();
    } else {
      parentNode.replace(table);
    }
  },
  type: 'element',
};

export const MARKDOWN_TRANSFORMERS: Transformer[] = [
  CHECK_LIST, // Must be first so `[] `, `[ ] `, `- [ ]` match before UNORDERED_LIST
  HEADING,
  QUOTE,
  CODE,
  TABLE, // GFM tables — before lists; only `| … |` lines match its regExp
  HORIZONTAL_RULE, // ---, ***, ___ for round-trip (export + import)
  // Standard UNORDERED_LIST: `- ` converts to a bullet live (conventional). CHECK_LIST
  // is listed first so `[ ]`/`[x]`/`- [ ]` still match as a checklist on import.
  UNORDERED_LIST,
  ORDERED_LIST,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  INLINE_CODE_FULL, // Before INLINE_CODE so full `...` is matched (no orphan closing backtick)
  INLINE_CODE,
  STRIKETHROUGH,
  LINK,
  UNDERLINE,
  SUBSCRIPT,
  SUPERSCRIPT,
];
